import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";

const Divisions = () => {
  const { t } = useLang();
  const divisions = t.divisions.list;
  const [active, setActive] = useState(0);
  const cur = divisions[active];

  return (
    <section
      id="divisions"
      data-testid="divisions-section"
      className="relative bg-white py-24 md:py-36 border-t border-[#E4E7ED]"
    >
      <div className="max-w-[1480px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 md:mb-20 items-end">
          <div className="lg:col-span-7">
            <p className="text-[11px] tracking-[0.35em] uppercase text-[#6F2DBD] font-semibold mb-6 flex items-center gap-3">
              <span className="inline-block w-10 h-px bg-[#6F2DBD]" />
              {t.divisions.overline}
            </p>
            <h2
              data-testid="divisions-title"
              className="font-display text-[#002B6B] text-4xl md:text-5xl lg:text-6xl leading-[1.02] uppercase"
            >
              {t.divisions.title}
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg text-[#6B7280] font-editorial font-light leading-relaxed">
              {t.divisions.lead}
            </p>
          </div>
        </div>

        {/* Selector + detail */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-[#E4E7ED] border border-[#E4E7ED]">
          {/* List */}
          <div className="lg:col-span-4 bg-white">
            <ul>
              {divisions.map((d, i) => {
                const isActive = i === active;
                return (
                  <li key={d.code}>
                    <button
                      data-testid={`division-${i}`}
                      onClick={() => setActive(i)}
                      className={`w-full text-left px-6 md:px-8 py-6 border-b border-[#E4E7ED] last:border-b-0 transition-colors group ${
                        isActive ? "bg-[#002B6B] text-white" : "bg-white text-[#1A1F2C] hover:bg-[#F7F8FA]"
                      }`}
                    >
                      <div className="flex items-start gap-5">
                        <span
                          className={`font-display text-2xl md:text-3xl leading-none ${
                            isActive ? "text-[#B89DDE]" : "text-[#6F2DBD]"
                          }`}
                        >
                          {d.code}
                        </span>
                        <div className="flex-1">
                          <p className={`text-[10px] tracking-[0.25em] uppercase font-bold ${
                            isActive ? "text-white/70" : "text-[#6F2DBD]"
                          }`}>
                            {d.tag}
                          </p>
                          <h3 className="font-display text-base md:text-lg mt-1.5 leading-tight">
                            {d.title}
                          </h3>
                        </div>
                        <span className={`text-base mt-1 transition-transform ${isActive ? "text-white translate-x-0.5" : "text-[#1A1F2C]/30"}`}>→</span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Detail */}
          <div className="lg:col-span-8 bg-[#F7F8FA] relative overflow-hidden min-h-[600px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
                className="absolute inset-0 flex flex-col"
                data-testid={`division-detail-${active}`}
              >
                <div className="relative h-56 md:h-72 overflow-hidden">
                  <img src={cur.image} alt={cur.title} loading="lazy" decoding="async" onError={(e) => { e.currentTarget.style.opacity = 0; }} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#002B6B]/85 via-[#002B6B]/35 to-transparent" />
                  <div className="absolute bottom-5 left-6 md:left-8 right-6 md:right-8 flex items-end justify-between text-white">
                    <span className="text-[10px] tracking-[0.3em] uppercase font-bold">{cur.tag}</span>
                    <span className="font-display text-3xl md:text-4xl text-white">{cur.code}</span>
                  </div>
                </div>

                <div className="flex-1 p-8 md:p-12 overflow-y-auto">
                  <h4 className="font-display text-2xl md:text-3xl text-[#002B6B] uppercase tracking-tight">
                    {cur.title}
                  </h4>
                  <p className="mt-3 text-base text-[#6B7280] font-editorial font-light max-w-2xl">
                    {cur.lead}
                  </p>

                  {cur.services && (
                    <div className="mt-8">
                      <p className="text-[10px] tracking-[0.3em] uppercase text-[#6F2DBD] font-semibold mb-4">Servicios</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2.5">
                        {cur.services.map((s) => {
                          const m = s.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
                          const name = m ? m[1] : s;
                          const note = m ? m[2] : null;
                          return (
                            <div key={s} className="flex items-start gap-2.5 text-sm text-[#1A1F2C]">
                              <span className="mt-2 inline-block w-1 h-1 bg-[#6F2DBD] flex-shrink-0" />
                              <span className="font-light leading-snug">
                                {name}
                                {note && (
                                  <span className="block text-[10px] text-[#6B7280] font-normal italic tracking-wide mt-0.5">
                                    ({note})
                                  </span>
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {cur.groups && (
                    <div className="mt-8 space-y-7">
                      {cur.groups.map((g) => (
                        <div key={g.name}>
                          <p className="text-[10px] tracking-[0.3em] uppercase text-[#6F2DBD] font-semibold mb-3">{g.name}</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                            {g.items.map((i) => {
                              const m = i.match(/^(.*?)\s*\(([^)]+)\)\s*$/);
                              const name = m ? m[1] : i;
                              const note = m ? m[2] : null;
                              return (
                                <div key={i} className="flex items-start gap-2.5 text-sm text-[#1A1F2C]">
                                  <span className="mt-2 inline-block w-1 h-1 bg-[#002B6B] flex-shrink-0" />
                                  <span className="font-light leading-snug">
                                    {name}
                                    {note && (
                                      <span className="block text-[10px] text-[#6B7280] font-normal italic tracking-wide mt-0.5">
                                        ({note})
                                      </span>
                                    )}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Divisions;
